import { GEO_LATITUDE, GEO_LONGITUDE, MAPS_URL, ADDRESS_FULL, HOURS_LABEL, PHONE_DISPLAY, PHONE_TEL } from '../../../lib/businessInfo';

const EMBED_URL = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2000!2d${GEO_LONGITUDE}!3d${GEO_LATITUDE}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDbCsDA5JzM4LjciTiAxMjPCsDU0JzM5LjYiVw!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus`;

interface ContactHeroProps {
  theme?: 'light' | 'dark';
  eyebrow?: {
    icon?: string;
    text: string;
  };
  title?: string;
  titleAccent?: string;
  subtitle?: string;
}

export default function ContactHero({ eyebrow, title, titleAccent, subtitle }: ContactHeroProps) {
  return (
    <section className="relative bg-gradient-to-br from-slate-50 via-white to-amber-50/30 pt-32 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Text block — centered */}
        <div className="text-center mb-10">
          {eyebrow && (
            <div className="inline-flex items-center gap-2 bg-[#1A2332]/10 text-[#1A2332] px-4 py-2 rounded-full text-sm font-medium mb-6">
              {eyebrow.icon && <i className={`${eyebrow.icon} text-base`} aria-hidden="true"></i>}
              {eyebrow.text}
            </div>
          )}

          <h1 className="text-5xl md:text-6xl font-bold text-[#1A2332] mb-6 leading-tight">
            {title ?? "Let's Talk"}
            {titleAccent && (
              <span className="block text-[#E8B84B]">{titleAccent}</span>
            )}
          </h1>

          {subtitle && (
            <p className="text-xl text-slate-700 leading-relaxed max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        {/* Map + quick info side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">

          {/* Map — takes 2/3 width on desktop */}
          <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-lg border border-slate-100 h-72 lg:h-80">
            <iframe
              src={EMBED_URL}
              width="100%"
              height="100%"
              style={{ border: 0, display: 'block' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Rockhound Auto Location Map"
            />
          </div>

          {/* Quick info card — 1/3 width */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 flex items-center justify-center bg-[#E8B84B]/15 rounded-full">
                  <i className="ri-map-pin-2-fill text-[#E8B84B] text-base"></i>
                </div>
                <span className="font-semibold text-[#1A2332] text-sm">Our Location</span>
              </div>
              <p className="text-slate-700 text-sm leading-relaxed mb-5">{ADDRESS_FULL}</p>

              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#1A2332]/8 rounded-full">
                  <i className="ri-time-line text-[#1A2332] text-base"></i>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Hours</p>
                  <p className="text-slate-700 text-sm font-medium">{HOURS_LABEL}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 flex items-center justify-center bg-[#1A2332]/8 rounded-full">
                  <i className="ri-phone-line text-[#1A2332] text-base"></i>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Phone</p>
                  <a href={`tel:${PHONE_TEL}`} className="text-slate-700 text-sm font-medium hover:text-[#E8B84B] transition-colors whitespace-nowrap cursor-pointer">
                    {PHONE_DISPLAY}
                  </a>
                </div>
              </div>
            </div>

            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center justify-center gap-2 bg-[#1A2332] hover:bg-[#E8B84B] text-white hover:text-[#1A2332] font-semibold text-sm px-4 py-3 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-navigation-line"></i>
              Get Directions
            </a>
          </div>

        </div>
      </div>
    </section>
  );
}
